/* eslint-disable max-lines */
/* eslint-disable @next/next/no-img-element */
import { useRouter } from 'next/router';
import H2 from '@base/H2/H2';
import P1 from '@base/P1/P1';
import { Fonts, Spacings, mediaQuery } from '@styles/index';
import { useSelector, useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import uniq from 'lodash/uniq';
import H6 from '@base/H6/H6';
import { useState, useEffect, useCallback } from 'react';
import H3 from '@base/H3/H3';
import H5 from '@base/H5/H5';
import Dynamic from '@base/Dynamic/Dynamic';
import { formatTime, getDateMonthString, isSameDay } from '@utils/dates';
import Divider from '@base/Divider/Divider';
import Image from '@base/Image';
import Tags from '@base/Tags/Tags';
import Time from '@icon/Time';
import { getTimeForDate } from '@utils/textHelper';
import Carousel from '@comp/Carousel/Carousel';
import { getIsLoggedIn } from '@selectors/auth';
import { toggleEnrolledCourse } from '@actions/courses';
import { segment } from '@actions/analytics';
import { GTM_TAP_ENROLL } from '@constants/segment';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import useScholarshipTestInfo from '@api/hooks/scholarship/useScholarshipTestInfo';
import useScholarshipEventDetails from '@api/hooks/scholarship/useScholarshipEventDetails';
import { useIsMobile } from '@stores/AppCommonData';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { DEFAULT_CLASS_NAME } from './constants';
import { sortingLogic } from './utils';

const DetailsDrawer = Dynamic(() => import('./DetailsDrawer'));
const ShareDrawer = Dynamic(() => import('./ShareDrawer'));
const AdditionalInputs = Dynamic(() => import('./AdditionalInputs'));
const EventButtons = Dynamic(() => import('./EventButtons'));

const Wrapper = styled.div`
  height: 970px;
  background: ${({ $bgColor }) =>
    $bgColor ||
    'radial-gradient(100% 100% at 50% 100%, var(--color-i-violet-8) 0%, var(--color-i-black-4) 100%), linear-gradient(0deg, var(--color-i-white), var(--color-i-white))'};
  margin-bottom: 10px;
  position: relative;
  overflow: hidden;
  ${mediaQuery.uptoTablet} {
    padding: ${Spacings.SPACING_4B};
    padding-right: ${({ $isSingleMWCard }) =>
      $isSingleMWCard ? Spacings.SPACING_4B : Spacings.SPACING_0B};
  }
`;

const Ellipsis = styled.div`
  height: 200px;
  ${mediaQuery.uptoTablet} {
    height: 100px;
    margin-top: -20px;
  }
  position: relative;
  width: auto;
  img {
    object-fit: cover;
    object-position: bottom;
    ${mediaQuery.uptoTablet} {
      width: 720px;
    }
  }
`;

const Header = styled.div`
  text-align: center;
  p {
    font-size: ${Fonts.LARGE};
    margin-top: ${Spacings.SPACING_4B};
    margin-bottom: ${Spacings.SPACING_6B};
  }
`;

const EventIcon = styled.div`
  text-align: center;
  display: inline-block;
  position: relative;
  height: 80px;
  width: 80px;
  margin-bottom: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoMobile} {
    height: 64px;
    width: 64px;
  }
`;

const Goals = styled.div`
  max-width: 1136px;
  margin: auto;
  display: flex;
  overflow: auto;
  white-space: nowrap;
  scroll-behavior: smooth;
  ::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const GoalWrap = styled.div`
  display: flex;
  margin: 0 auto;
`;

const GoalPill = styled.div`
  margin-right: ${Spacings.SPACING_4B};
  padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_6B};
  border: 1px solid var(--color-text-secondary);
  border-radius: 4px;
  cursor: pointer;
  background-color: ${({ $isFilled }) =>
    $isFilled ? 'var(--color-base-1)' : 'transparent'};
`;

const EventsWrapper = styled.div`
  max-width: 1136px;
  margin: auto;
  margin-top: ${Spacings.SPACING_8B};
  ${mediaQuery.uptoTablet} {
    width: 100%;
    overflow: hidden;
  }
`;

const EventsMWWrapper = styled(EventsWrapper)`
  display: flex;
  overflow: scroll !important;
`;

const CardWrapper = styled.div`
  background: linear-gradient(
      179.92deg,
      rgba(214, 227, 242, 0.21) 0.07%,
      rgba(214, 227, 242, 0) 56.85%
    ),
    var(--color-base-1);
  border: 1px solid var(--color-base-1);
  box-sizing: border-box;
  box-shadow: ${boxShadowGenerator({ color: 'rgb(11 11 11 / 10%)' })};
  border-radius: 8px;
  margin-right: ${Spacings.SPACING_4B};
  padding: ${Spacings.SPACING_5B};
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  ${mediaQuery.uptoTablet} {
    width: ${({ $isSingleMWCard }) => ($isSingleMWCard ? '100%' : '90%')};
    min-width: 320px;
    max-width: 340px;
    margin: ${({ $isSingleMWCard }) => $isSingleMWCard && 'auto'};
    margin-right: ${({ $isSingleMWCard }) =>
      $isSingleMWCard && Spacings.SPACING_4B};
  }
`;

const SingleCardWrapper = styled.div`
  display: flex;
  width: max-content;
  margin: auto;
  padding: ${Spacings.SPACING_11B} ${Spacings.SPACING_14B};
  background: linear-gradient(
      179.92deg,
      rgba(214, 227, 242, 0.21) 0.07%,
      rgba(214, 227, 242, 0) 56.85%
    ),
    var(--color-base-1);
  border: 1px solid var(--color-base-1);
  box-sizing: border-box;
  box-shadow: ${boxShadowGenerator({ color: 'rgb(11 11 11 / 10%)' })};
  border-radius: 8px;
`;

const VerticalDivider = styled.div`
  width: 2px;
  background-color: var(--color-divider);
  margin-left: 80px;
  margin-right: 56px;
`;

const RightContent = styled.div`
  margin: auto;
`;

const DateText = styled(H5)`
  margin-right: ${Spacings.SPACING_1B};
  color: ${({ $isLive }) =>
    $isLive ? 'var(--color-i-red)' : 'var(--color-text-primary)'};
`;

const StyledDivider = styled(Divider)`
  margin: ${Spacings.SPACING_4B} auto;
`;

const Flex = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: ${Spacings.SPACING_3B};
  h6 {
    margin-left: ${Spacings.SPACING_5B};
  }
`;

const DoubleCardWrapper = styled.div`
  display: flex;
  max-width: 800px;
  margin: auto;
`;

const LiveTag = styled(Tags)`
  margin-right: ${Spacings.SPACING_2B};
`;

const DateWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${Spacings.SPACING_3B};
`;

const Events = ({ enrolledEventIds, setEnrolledEventIds }) => {
  const dispatch = useDispatch();
  const {
    query: { eventKey }
  } = useRouter();
  const { prodigyInfo } = useScholarshipTestInfo(eventKey);
  const { prodigyDetails: extraDetailsPresent } =
    useScholarshipEventDetails(eventKey);

  const isMobile = useIsMobile();

  const { eventsMeta, events, title, themeBg, additionalInputs } = prodigyInfo;

  const goals = uniq(events.map((el) => el.goalKey)) || [];
  const eventsData = sortingLogic(events, goals);
  const enrolledGoalKey = events.find((event) =>
    enrolledEventIds.includes(event.testSeriesUid)
  )?.goalKey;
  const defaultGoal =
    enrolledGoalKey ||
    goals.find((goal) => goal === DEFAULT_CLASS_NAME) ||
    goals[0];

  const isLoggedIn = useSelector(getIsLoggedIn);
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const [currGoal, setCurrGoal] = useState(defaultGoal);
  const [currEvent, setCurrentEvent] = useState(null);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [enrollClicked, setEnrollClicked] = useState(false);
  const [additionalDetailsOpen, setAdditionalDetailsOpen] = useState(false);

  useEffect(() => {
    if (enrolledGoalKey) setCurrGoal(enrolledGoalKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrolledEventIds]);

  function gtag_report_conversion() {
    if (window.gtag)
      window.gtag('event', 'conversion', {
        send_to: 'AW-10852057838/ferOCMqC3_sYEO6F1bYo', // pragma: allowlist secret
        event_callback: () => {}
      });
    return false;
  }

  const enrollAction = useCallback(
    (event = currEvent) => {
      dispatch(
        toggleEnrolledCourse(event.testSeriesUid, true, true, eventKey)
      ).then((response) => {
        if (!response.error) {
          setEnrolledEventIds((ids) =>
            ids.includes(event.testSeriesUid)
              ? ids
              : [...ids, event.testSeriesUid]
          );
          setShareDialogOpen(true);
          setEnrollClicked(false);
          const analyticsProps = {
            test_name: event.testName,
            test_uid: event.testUid,
            start_time: event.startsAt,
            end_time: event.endsAt,
            Subject: event.topics,
            test_series_uid: event.testSeriesUid,
            gtm_initiative: title || '',
            goal_id: '',
            goal_name: ''
          };
          segment.track(GTM_TAP_ENROLL, analyticsProps);
          gtag_report_conversion();
        }
      });
    },
    [currEvent, dispatch, eventKey, title, setEnrolledEventIds]
  );

  const onClickEnroll = useCallback(
    (event = currEvent) => {
      if (event) {
        setEnrollClicked(true);
        setCurrentEvent(event);
        if (!isLoggedIn) {
          openLoginDrawer();
          return;
        }
        if (enrolledEventIds.includes(event.testSeriesUid)) return;
        if (
          additionalInputs?.length &&
          !extraDetailsPresent?.userResponse?.questionsSubmitted
        ) {
          setAdditionalDetailsOpen(true);
        } else {
          enrollAction(event);
        }
      }
    },
    [
      additionalInputs,
      currEvent,
      enrollAction,
      extraDetailsPresent,
      enrolledEventIds,
      isLoggedIn,
      openLoginDrawer
    ]
  );

  const onTestClick = useCallback(
    (event = currEvent) => {
      if (event) {
        if (
          additionalInputs?.length &&
          !extraDetailsPresent?.userResponse?.questionsSubmitted
        ) {
          setAdditionalDetailsOpen(true);
        } else {
          window.open(event.permalink);
        }
      }
    },
    [additionalInputs, currEvent, extraDetailsPresent]
  );

  useEffect(() => {
    if (enrollClicked && isLoggedIn) {
      onClickEnroll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  if (!eventsData[currGoal]) return null;

  const renderEventPointers = (event) => {
    return (
      <>
        <Flex>
          <Time size="24px" color="var(--color-text-secondary)" />
          <H6 color="var(--color-text-secondary)">{event.durationText}</H6>
        </Flex>
        <Flex>
          <Image
            src="prodigy/book-grey-outline.svg"
            alt="book"
            width={24}
            height={24}
          />
          <H6 color="var(--color-text-secondary)" lineClamp={2}>
            {event.topicsShort}
          </H6>
        </Flex>
        <Flex>
          <Image
            src="prodigy/question-circle-outline.svg"
            alt="book"
            width={24}
            height={24}
          />
          <H6 color="var(--color-text-secondary)">
            {event.numberOfRounds && event.numberOfRounds > 1
              ? `${event.numberOfRounds} rounds · ${event.totalQuestions} questions each`
              : `Total of ${event.totalQuestions} questions`}
          </H6>
        </Flex>
      </>
    );
  };

  const renderDateLine = (event) => {
    const startTimeDiff =
      new Date(getTimeForDate(event.startsAt, event.date)) - new Date();
    const endTimeDiff =
      new Date(getTimeForDate(event.endsAt, event.date)) - new Date();
    const isLive =
      isSameDay(new Date(event.date), new Date()) &&
      startTimeDiff < 1800000 &&
      endTimeDiff > 0;
    let liveTag = isLive
      ? `${startTimeDiff < 0 ? 'Started' : 'Starts'} at ${formatTime(
          getTimeForDate(event.startsAt, event.date)
        )}`
      : getDateMonthString(new Date(event.date));
    if (endTimeDiff < 0)
      liveTag = `Conducted on ${getDateMonthString(
        new Date(event.date)
      )}, ${formatTime(getTimeForDate(event.startsAt, event.date))}`;
    return (
      <DateWrapper>
        {isLive && <LiveTag type="live">LIVE</LiveTag>}
        <DateText>{liveTag}</DateText>
        {endTimeDiff > 0 && !isLive && (
          <DateText>
            {` • ${formatTime(
              getTimeForDate(event.startsAt, event.date)
            )} - ${formatTime(getTimeForDate(event.endsAt, event.date))}`}
          </DateText>
        )}
      </DateWrapper>
    );
  };

  const renderCard = (event) => {
    const startTimeDiff =
      new Date(getTimeForDate(event.startsAt, event.date)) - new Date() <=
      1800000;
    return (
      <CardWrapper
        key={`event-${event.id}`}
        gridColumn="span 8"
        $isSingleMWCard={eventsData[currGoal].length === 1 && isMobile}
      >
        <div>
          <H3>{event.testName}</H3>
          {renderDateLine(event)}
          <StyledDivider />
          {renderEventPointers(event)}
        </div>
        <EventButtons
          event={{
            ...event,
            isEnrolled: enrolledEventIds.includes(event.testSeriesUid)
          }}
          onClickEnroll={startTimeDiff ? onTestClick : onClickEnroll}
          setCurrentEvent={setCurrentEvent}
          setDrawerOpen={setDrawerOpen}
        />
      </CardWrapper>
    );
  };

  const renderSingleCard = (event) => {
    return (
      <SingleCardWrapper key={`event-${event.id}`} gridColumn="span 8">
        <div>
          <H3>{event.testName}</H3>
          {renderDateLine(event)}
          <EventButtons
            event={{
              ...event,
              isEnrolled: enrolledEventIds.includes(event.testSeriesUid)
            }}
            onClickEnroll={onClickEnroll}
            setCurrentEvent={setCurrentEvent}
            setDrawerOpen={setDrawerOpen}
          />
        </div>
        <VerticalDivider />
        <RightContent>{renderEventPointers(event)}</RightContent>
      </SingleCardWrapper>
    );
  };

  const isServer = typeof window === 'undefined';

  return (
    <Wrapper
      $isSingleMWCard={eventsData[currGoal].length === 1 && isMobile}
      $bgColor={themeBg}
    >
      <Ellipsis>
        <Image src="prodigy/ellipsis.png" alt="" layout="fill" />
      </Ellipsis>
      <Header>
        <EventIcon>
          <Image src={eventsMeta.image} alt="-" layout="fill" />
        </EventIcon>
        <H2 color="var(--color-base-1)">{eventsMeta.title}</H2>
        <P1 color="var(--color-base-1)">{eventsMeta.subTitle}</P1>
      </Header>
      <Goals>
        <GoalWrap>
          {goals.map((goal) => {
            return (
              <GoalPill
                key={goal}
                onClick={() => setCurrGoal(goal)}
                $isFilled={goal === currGoal}
              >
                <H6
                  color={
                    goal === currGoal
                      ? 'var(--color-text-primary)'
                      : 'var(--color-base-1)'
                  }
                >
                  {goal}
                </H6>
              </GoalPill>
            );
          })}
        </GoalWrap>
      </Goals>
      {!isServer && (
        <>
          {isMobile ? (
            <EventsMWWrapper>
              {eventsData[currGoal].map((event) => {
                return renderCard(event);
              })}
            </EventsMWWrapper>
          ) : (
            <EventsWrapper>
              {eventsData[currGoal].length === 1 &&
                renderSingleCard(eventsData[currGoal][0])}
              {eventsData[currGoal].length === 2 && (
                <DoubleCardWrapper>
                  {eventsData[currGoal].map((event) => {
                    return renderCard(event);
                  })}
                </DoubleCardWrapper>
              )}
              {eventsData[currGoal].length > 2 && (
                <Carousel
                  numberOfItems={3}
                  paddingBottom="0"
                  key={`goal-${currGoal}`}
                >
                  {eventsData[currGoal].map((event) => {
                    return renderCard(event);
                  })}
                </Carousel>
              )}
            </EventsWrapper>
          )}
        </>
      )}
      {currEvent && isDrawerOpen && (
        <DetailsDrawer
          currEvent={currEvent}
          isDrawerOpen={isDrawerOpen}
          setDrawerOpen={setDrawerOpen}
        />
      )}
      {additionalDetailsOpen && (
        <AdditionalInputs
          drawerState={additionalDetailsOpen}
          closeDrawerHandler={() => setAdditionalDetailsOpen(false)}
          additionalInputs={additionalInputs}
          eventKey={eventKey}
          events={events}
          callback={enrollAction}
        />
      )}
      {currEvent && shareDialogOpen && (
        <ShareDrawer
          showShareDialog={shareDialogOpen}
          closeShareDialog={() => setShareDialogOpen(false)}
          event={currEvent}
          enrolledEventIds={enrolledEventIds}
        />
      )}
    </Wrapper>
  );
};

export default Events;
